/**
 * Lógica pura del panel de diagnóstico SMTP: sin DOM y sin jQuery, para poder
 * probarla. Pinta el resultado de las dos pruebas de v2/smtp: «Probar conexión»
 * (lo capturado, hasta AUTH) y «Probar y reactivar mi correo» (lo guardado, hasta
 * MAIL FROM del remitente; su resultado trae `etapas`). etiquetaEstado se conserva
 * por si se cablea el estado; la vista no la usa.
 */

/** Todas las etapas posibles, en orden. 'remitente' (MAIL FROM + RSET) solo la recorre la prueba de la
 *  configuración guardada (v2/smtp/verificar_ajax); el resultado trae en `etapas` las que recorrió. */
export var ETAPAS = ['dns', 'conexion', 'saludo', 'cifrado', 'auth', 'remitente'];

/** Las de «Probar conexión» (probar_ajax), que termina en AUTH: lo que se pinta si el resultado no trae `etapas`. */
export var ETAPAS_BASE = ['dns', 'conexion', 'saludo', 'cifrado', 'auth'];

export var ROTULOS = {
    dns: 'Encontramos tu servidor',
    conexion: 'Conexión establecida',
    saludo: 'Tu servidor respondió',
    cifrado: 'Cifrado activo',
    auth: 'Credenciales aceptadas',
    remitente: 'Remitente aceptado'
};

/**
 * La etapa donde se detuvo la prueba, dicha como lo que pasó (antes se pintaba con su
 * rótulo de éxito tachado: «✗ Encontramos tu servidor»).
 */
export var ROTULOS_FALLA = {
    dns: 'No encontramos tu servidor',
    conexion: 'No logramos conectarnos con tu servidor',
    saludo: 'Tu servidor no respondió como servidor de correo',
    cifrado: 'No logramos cifrar la conexión',
    auth: 'Tu servidor no aceptó el usuario o la contraseña',
    remitente: 'Tu servidor no aceptó el remitente'
};

export var PROVEEDORES = [
    { patron: /gmail|google/i,
      texto: 'Gmail ya no acepta la contraseña normal de tu cuenta desde 2022. Necesitas una <b>contraseña de aplicación</b>.' },
    { patron: /office365|outlook|hotmail|live\\.com/i,
      texto: 'Microsoft ya no acepta la contraseña normal de tu cuenta. Necesitas una <b>contraseña de aplicación</b>.' }
];

/** Escapa texto que viene del servidor del cliente antes de pintarlo. */
export function esc(s) {
    if (s === null || s === undefined) { return ''; }
    return String(s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export function avisoProveedor(host) {
    for (var i = 0; i < PROVEEDORES.length; i++) {
        if (PROVEEDORES[i].patron.test(host || '')) { return PROVEEDORES[i].texto; }
    }
    return null;
}

/**
 * Devuelve el HTML del panel. Las etapas posteriores a la que falló no se
 * pintan: no se intentaron, y mostrarlas en gris sugeriría que se probaron.
 */
export function renderDiagnostico(r) {
    var lista = (r.etapas && r.etapas.length)
        ? ETAPAS.filter(function (e) { return r.etapas.indexOf(e) !== -1; })
        : ETAPAS_BASE;
    var corte = lista.indexOf(r.etapa);
    var html = '<div class="border rounded p-3">';

    lista.forEach(function (e, i) {
        var superada = r.ok || corte === -1 || i < corte;
        if (!superada && i > corte) { return; }
        html += '<div class="d-flex align-items-baseline gap-2 py-1">'
             +  '<span class="' + (superada ? 'text-success' : 'text-danger') + '">'
             +  (superada ? '&#10003;' : '&#10007;') + '</span>'
             +  '<span class="small' + (superada ? '' : ' fw-semibold text-danger') + '">'
             +  esc(superada ? ROTULOS[e] : ROTULOS_FALLA[e]) + '</span></div>';
    });

    if (r.ok) {
        html += '<div class="alert alert-success mt-2 mb-0">'
             +  '<div class="fw-semibold"><i class="ti ti-circle-check me-1" aria-hidden="true"></i>La conexión funciona</div>'
             +  '<div class="small">' + (lista.indexOf('remitente') !== -1 ? 'Autenticación y remitente aceptados. ' : 'Autenticación aceptada. ')
             +  esc(r.ms) + ' ms · no se envió ningún correo.</div></div>';
        return html + '</div>';
    }

    html += '<div class="alert alert-danger mt-2 mb-0">'
         +  '<div class="fw-semibold">' + esc(r.titulo) + '</div>';
    if (r.mensaje) {
        html += '<div class="small text-uppercase fw-bold mt-2">Respuesta de tu servidor</div>'
             +  '<code class="d-block bg-white border rounded p-2 mt-1">' + esc(r.mensaje) + '</code>';
    }
    // significa y arreglo los compone el servidor con html_escape sobre los datos
    // del emisor, y llevan <b> intencional: se insertan sin re-escapar.
    if (r.significa) {
        html += '<div class="small text-uppercase fw-bold mt-2">Qué significa</div>'
             +  '<div class="small">' + r.significa + '</div>';
    }
    if (r.arreglo && r.arreglo.length) {
        html += '<div class="small text-uppercase fw-bold mt-2">Cómo se arregla</div><ul class="small mb-0">';
        r.arreglo.forEach(function (a) { html += '<li>' + a + '</li>'; });
        html += '</ul>';
    }
    html += '<button type="button" id="btn-copiar-diag" class="btn btn-sm btn-outline-secondary mt-3">'
         +  '<i class="ti ti-copy"></i> Copiar diagnóstico</button></div>';

    return html + '</div>';
}

/**
 * Único mapeo estado → insignia. Esta pieza no lo cablea (stateless);
 * se conserva para no reescribirlo en la pieza 2.
 */
export var ETIQUETA_ESTADO = {
    verificada:    { clase: 'bg-success',   texto: 'Verificada',
                     nota: 'Tus CFDIs salen por tu servidor.' },
    fallando:      { clase: 'bg-danger',    texto: 'Fallando',
                     nota: 'Tus CFDIs están en espera hasta que tu servidor vuelva a funcionar.' },
    sin_verificar: { clase: 'bg-secondary', texto: 'Sin verificar',
                     nota: 'Tus CFDIs salen por Factura Digital.' }
};

export function etiquetaEstado(estado) {
    return ETIQUETA_ESTADO[estado] || ETIQUETA_ESTADO.sin_verificar;
}

/** Texto plano para el botón de copiar: nunca incluye la contraseña. */
export function textoParaCopiar(r) {
    return ['Diagnóstico SMTP · Factura Digital',
            'Etapa: ' + (r.etapa || ''),
            'Servidor dijo: ' + (r.mensaje || '—'),
            '', 'Traza:'].concat(r.traza || []).join('\n');
}
